import {
  InfiniteData,
  infiniteQueryOptions,
  QueryClient,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query'

import { schemas, ZInfer } from '@showrunner/scrapi'

import {
  fetchActiveRevisionSnapshot,
  type ActiveRevisionSnapshotLoader,
} from '#choo-app/lib/editor/plugins/revisions'
import { useMst } from '#state'
import { ScrapiClient } from '#util'
import { extractTsRestSuccess } from '#util/extractTsRest'
import {
  ScriptSnapshotPayload,
  SnapshotAutoSave,
} from '#util/ScriptoApiClient/types'

/*
  notes about staleTime/gcTime

  staleTime means: "How long is this query considered valid"
  gcTime means: "When no longer mounted, when should it be garbage collected"

  While in snapshot land for a script, we need to balance between reusing API
  responses and using too much memory.

  For the "current version" of the script, we should consider the value to
  be invalid pretty quickly, but it's still in discussion how this should work

  Snapshots on the other hand are immutable, so staleTime is Infinity, but we don't
  want to over-cache them.

*/
const minutesToMillis = (minutes: number) => minutes * 60 * 1000

/**
 * Individual snapshots
 *
 * Snapshots are immutable, so we can cache them indefinitely. It is possible for
 * a snapshot to be the "current script", which is not a snapshot at all, but a
 * script doc wrapped in the snapshot interface, and this must not be cached.
 */

interface SnapshotQueryOptions {
  scriptId: string
  snapshotId: string
}

export function snapshotQueryKey(options: SnapshotQueryOptions) {
  return ['snapshotland-snapshot', options]
}

/**
 * Fetch a snapshot from the api. Will synthesize a "current script" snapshot
 * timestamped to whenever it was last fetched.
 */
async function fetchSnapshot(
  scrapi: ScrapiClient,
  queryClient: QueryClient,
  options: SnapshotQueryOptions,
): Promise<ScriptSnapshotPayload> {
  if (options.snapshotId === 'current') {
    const scriptOptions = scriptQueryOptions(scrapi, options.scriptId)

    const data = await queryClient.ensureQueryData(scriptOptions)
    const queryCache = queryClient.getQueryCache()
    const queryData = queryCache.find(scriptOptions)
    // this should always be the time when it was last fetched since we are
    // first waiting for the query to execute via `ensureQueryData`. The
    // `Date.now()` is a fallback so we don't need to handle a null:
    const dataUpdatedAt = queryData?.state.dataUpdatedAt ?? Date.now()

    return {
      id: 'current',
      name: 'Current script',
      scriptId: data.script.id,
      doc: data.script.doc,
      version: data.script.version,
      scriptFormat: data.script.scriptFormat,
      createdAt: new Date(dataUpdatedAt),
      createdBy: '',
      creatorName: '',
      autoSave: 'backup',
    }
  }

  const fn = scrapi.scripts.getSnapshot({ params: options })
  const { body } = await extractTsRestSuccess(fn, 200)
  return body
}

export function snapshotRevisionsLoader(
  scrapi: ScrapiClient,
  snapshot: Pick<ScriptSnapshotPayload, 'id' | 'scriptId' | 'revisions'>,
): ActiveRevisionSnapshotLoader {
  return snapshot.id === 'current'
    ? () => fetchActiveRevisionSnapshot(scrapi, snapshot.scriptId)
    : async () => snapshot.revisions ?? null
}

function snapshotQueryOptions(
  scrapi: ScrapiClient,
  queryClient: QueryClient,
  options: SnapshotQueryOptions,
) {
  const { snapshotId } = options

  return queryOptions({
    queryKey: snapshotQueryKey(options),
    queryFn: () => fetchSnapshot(scrapi, queryClient, options),
    // if we have a snapshot in the cache, it is not stale
    // when we're loading the current script, we should not cache
    staleTime: snapshotId === 'current' ? 0 : Infinity,
    // keep for 5 minutes if a snapshot is unmounted
    gcTime: snapshotId === 'current' ? 0 : minutesToMillis(5),
  })
}

export const useSnapshotData = ({
  snapshotId,
  scriptId,
}: SnapshotQueryOptions) => {
  const queryClient = useQueryClient()

  return useQuery({
    ...snapshotQueryOptions(useMst().scrapi, queryClient, {
      snapshotId,
      scriptId,
    }),
  })
}

export const useMaybeSnapshot = ({
  snapshotId,
  scriptId,
}: {
  snapshotId?: string
  scriptId: string
}) => {
  const { scrapi, doDebug } = useMst()
  const queryClient = useQueryClient()

  return useQuery({
    queryFn: async () => {
      await doDebug()
      if (!snapshotId) return null
      return fetchSnapshot(scrapi, queryClient, { scriptId, snapshotId })
    },
    queryKey: snapshotQueryKey({ snapshotId: snapshotId ?? 'none', scriptId }),
    // if we have a snapshot in the cache, it is not stale
    // when we're loading the current script, we should not cache
    staleTime: snapshotId === 'current' ? 0 : Infinity,
    // keep for 5 minutes if a snapshot is unmounted
    gcTime: snapshotId === 'current' ? 0 : minutesToMillis(5),
  })
}

export const useSelectedSnapshots = (scriptId: string) => {
  const { snap1, snap2 } = useMst().view.snapshotLand
  const topQuery = useMaybeSnapshot({ scriptId, snapshotId: snap1 })
  const bottomQuery = useMaybeSnapshot({ scriptId, snapshotId: snap2 })

  return {
    topQuery,
    bottomQuery,
  }
}

/**
 * Fetch an individual script
 */

export function scriptQueryKey(scriptId: string) {
  return ['snapshotland-script', scriptId]
}

function scriptQueryOptions(scrapi: ScrapiClient, scriptId: string) {
  return queryOptions({
    queryKey: scriptQueryKey(scriptId),
    queryFn: async () => {
      const fn = scrapi.scripts.getScript({ params: { id: scriptId } })
      const { body } = await extractTsRestSuccess(fn, 200)

      return { script: body }
    },
  })
}

export const useScriptData = (scriptId: string) => {
  const mst = useMst()

  return useQuery({
    ...scriptQueryOptions(mst.scrapi, scriptId),
    // TODO: revisit these
    staleTime: Infinity,
    gcTime: 0,
  })
}

export function useCurrentScriptSnapshotHasNewer(options: {
  scriptId: string
  refetchInterval: number
}) {
  const mst = useMst()

  const { data: snapshotData } = useSnapshotData({
    scriptId: options.scriptId,
    snapshotId: 'current',
  })

  const { data: latestVersion } = useQuery({
    ...scriptQueryOptions(mst.scrapi, options.scriptId),
    select: ({ script }) => script.version,
    refetchInterval: options.refetchInterval,
  })

  return snapshotData && latestVersion && latestVersion > snapshotData.version
}

export function snapshotHistoryQueryKey(scriptId: string, filter?: string) {
  if (filter) return ['snapshot-history', { scriptId, filter }]
  return ['snapshot-history', { scriptId }]
}

function snapshotHistoryQueryOptions(
  scrapi: ScrapiClient,
  options: { scriptId: string; filter: 'all' | 'manual'; perPage: number },
) {
  return infiniteQueryOptions({
    queryKey: snapshotHistoryQueryKey(options.scriptId, options.filter),
    queryFn: async (context: { pageParam: string | null }) => {
      const request = scrapi.scripts.listSnapshots({
        params: { id: options.scriptId },
        query:
          context.pageParam === null
            ? { perPage: options.perPage, filter: options.filter }
            : {
                cursor: context.pageParam,
                perPage: options.perPage,
                filter: options.filter,
              },
      })

      const { body } = await extractTsRestSuccess(request, 200)
      const total = body.results.length
      return { ...body, total }
    },
    // The server returns a `nextCursor` when there are exactly zero more records available.
    // here we check the accumulated count to avoid suggesting the user load an empty page
    // https://github.com/showrunner/nido/pull/355#discussion_r2040549425
    getNextPageParam(lastPage, allPages) {
      const loadedCount = allPages
        .map((page) => page.results.length)
        .reduce((a, b) => a + b, 0)

      return loadedCount < lastPage.pagination.totalRecords
        ? lastPage.pagination.nextCursor
        : null // nothing left to fetch
    },
    initialPageParam: null,
    select: consolidatePages,
  })
}

export function useSnapshotHistoryQuery(scriptId: string) {
  const { scrapi, view, user } = useMst()
  const filter = user.prefs.snapshotFilter ?? 'all'

  const perPage = view.isDebugEnabled('small-page') ? 5 : 50

  return useSuspenseInfiniteQuery({
    ...snapshotHistoryQueryOptions(scrapi, { scriptId, filter, perPage }),
    staleTime: 5 * 60 * 1000,
  })
}

export type SnapshotHistoryPage = ZInfer<typeof schemas.ListSnapshotsResponse>

// Don't inline - must be a stable reference
function consolidatePages(data: InfiniteData<SnapshotHistoryPage>) {
  return {
    snapshots: data.pages.flatMap((page) => page.results),
    total: Math.max(...data.pages.map((page) => page.pagination.totalRecords)),
  }
}

function snapshotHistoryCountOptions(
  scrapi: ScrapiClient,
  scriptId?: string,
  filter: 'all' | 'manual' = 'all',
) {
  return queryOptions({
    queryKey: ['snapshot-history-count', { scriptId, filter }],
    queryFn: async () => {
      if (!scriptId) return 0

      const request = scrapi.scripts.listSnapshots({
        params: { id: scriptId },
        query: { perPage: 1, filter },
      })

      const { body } = await extractTsRestSuccess(request, 200)

      return body.pagination.totalRecords
    },
    initialData: 0,
  })
}

export function useSnapshotHistoryHasNewer(options: {
  scriptId: string
  refetchInterval: number
}) {
  const { scrapi, view } = useMst()
  const { filter } = view.snapshotLand

  const { data: count } = useQuery({
    ...snapshotHistoryCountOptions(scrapi, options.scriptId, filter),
    refetchInterval: options.refetchInterval,
  })

  const { data } = useInfiniteQuery({
    ...snapshotHistoryQueryOptions(scrapi, {
      scriptId: options.scriptId,
      filter,
      perPage: 50,
    }),
  })

  return data ? count > data.total : false
}

export async function createSnapshot(
  scrapi: ScrapiClient,
  params: {
    scriptId: string
    name?: string
    autoSave?: SnapshotAutoSave
  },
): Promise<{ id: string }> {
  const { scriptId, ...data } = params

  if (!data.name && !data.autoSave) {
    throw new Error(`Either 'name' or 'autoSave' must be set`)
  }

  const { body } = await extractTsRestSuccess(
    scrapi.scripts.createSnapshot({
      params: { scriptId },
      body: data,
    }),
    201,
  )

  return body
}

export async function renameSnapshots(
  scrapi: ScrapiClient,
  {
    scriptId,
    snapshotId,
    name,
  }: {
    scriptId: string
    snapshotId: string
    name: string
  },
): Promise<{ id: string }> {
  const { body } = await extractTsRestSuccess(
    scrapi.scripts.renameSnapshot({
      params: { scriptId, snapshotId },
      body: { name },
    }),
    200,
  )

  return body
}
